import React, { useCallback, useRef } from 'react';
import { Cell, Row, useTable } from 'react-table';
import BTable from 'react-bootstrap/Table';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import { DataTableProps } from './DataTable';

const DND_ITEM_TYPE = 'row';

interface ItemWithId {
    id: string;
}

export interface DraggableType extends ItemWithId {
    draggable: boolean;
}

type RowProps<T extends DraggableType> = {
    row: Row<T>;
    moveRow: Function;
};

const DraggableRow = <T extends DraggableType>({ row, moveRow }: RowProps<T>) => {
    const ref = useRef(null);
    const [, connectDropRef] = useDrop({
        accept: DND_ITEM_TYPE,
        hover(draggedRow: Row<T>) {
            if (draggedRow.id !== row.id) {
                moveRow(draggedRow.id, row.id);
            }
        },
    });

    const [{ isDragging }, dragRef, connectPreviewRef] = useDrag({
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        item: () => row,
        type: DND_ITEM_TYPE,
    });

    const opacity = isDragging ? 0.5 : 1;

    const { draggable } = row.original;

    connectDropRef(ref);
    connectPreviewRef(ref);
    return draggable ? (
        <tr ref={ref} style={{ opacity }}>
            <td>
                <i ref={dragRef} className="fas fa-bars" />
            </td>
            {row.cells.map((cell: Cell<T, any>) => (
                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
            ))}
        </tr>
    ) : (
        <tr>
            <td>
                <i className="fas fa-lock" />
            </td>
            {row.cells.map((cell: Cell<T, any>) => (
                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
            ))}
        </tr>
    );
};

interface DraggableTableProps<T extends DraggableType> extends DataTableProps<T> {
    setData: Function;
}

const DataTableDraggable = <T extends DraggableType>(props: DraggableTableProps<T>) => {
    const { columns, data: records, setData: setRecords } = props;

    const getRowId = useCallback((row: T) => row.id, []);

    const moveRow = (draggedId: string, hoverId: string) => {
        const draggedIndex = records.findIndex((record) => record.id === draggedId);
        const hoverIndex = records.findIndex((record) => record.id === hoverId);
        const draggedRecord = records[draggedIndex];

        const afterReorder = update(records, {
            $splice: [
                [draggedIndex, 1],
                [hoverIndex, 0, draggedRecord],
            ],
        });

        const afterReCalculateStopNumber = afterReorder.map((item, idx) => ({
            ...item,
            stopNumber: idx + 1,
        }));

        setRecords(afterReCalculateStopNumber);
    };

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
        data: records,
        columns,
        getRowId,
    });

    return (
        <DndProvider backend={HTML5Backend}>
            <BTable size="sm" {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            <th> </th>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                        prepareRow(row);
                        return <DraggableRow row={row} moveRow={moveRow} {...row.getRowProps()} />;
                    })}
                    {rows.length <= 0 && (
                        <tr>
                            <td colSpan={1000} className="text-center">
                                <i>No data to display.</i>
                            </td>
                        </tr>
                    )}
                </tbody>
            </BTable>
        </DndProvider>
    );
};

export default DataTableDraggable;
